'use client'

import React, { createContext, useContext, useState } from 'react'
import type { CourierChatState, OrderId } from '@/help-center/entities'
import { getCourierChatState, getCourierChatUrl } from '@/help-center/use-cases'

export interface CourierChatContextType {
  courierChatIframeUrl: CourierChatState['courierChatIframeUrl']
  isCourierChatVisible: CourierChatState['isCourierChatVisible']
  chats: CourierChatState['chats']
  chatConfig: CourierChatState['chatConfig']
  fetchCourierChatIframeUrl: () => Promise<string>
  fetchCourierChatState: () => Promise<void>
  openCourierChat: (orderId: OrderId) => void
  closeCourierChat: () => void
}

export const CourierChatContext = createContext<CourierChatContextType>({
  courierChatIframeUrl: null,
  isCourierChatVisible: false,
  chats: [],
  chatConfig: {},
  fetchCourierChatIframeUrl: async () => '',
  fetchCourierChatState: async () => {},
  openCourierChat: () => {},
  closeCourierChat: () => {},
})

export const CourierChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [courierChatState, setCourierChatState] = useState<CourierChatState>({
    courierChatIframeUrl: null,
    isCourierChatVisible: false,
    orderId: null,
    chats: [],
    chatConfig: {},
  })

  const { courierChatIframeUrl, isCourierChatVisible, chats, chatConfig } = courierChatState

  const fetchCourierChatIframeUrl = async () => {
    const courierChatIframeUrlResponse = await getCourierChatUrl({
      orderId: courierChatState.orderId ?? undefined,
    })
    setCourierChatState({
      ...courierChatState,
      courierChatIframeUrl: courierChatIframeUrlResponse.data.url,
      chatConfig: courierChatIframeUrlResponse.data.chatConfig,
    })
    return courierChatIframeUrlResponse.data.url
  }

  const fetchCourierChatState = async () => {
    const chatStateResponse = await getCourierChatState()
    setCourierChatState({
      ...courierChatState,
      chats: chatStateResponse.data.chats,
    })
  }

  const openCourierChat = (orderId: OrderId) => {
    setCourierChatState({
      ...courierChatState,
      isCourierChatVisible: true,
      orderId,
    })
  }

  const closeCourierChat = () => {
    setCourierChatState({
      ...courierChatState,
      courierChatIframeUrl: null,
      isCourierChatVisible: false,
      orderId: null,
    })
  }

  return (
    <CourierChatContext.Provider
      value={{
        courierChatIframeUrl,
        isCourierChatVisible,
        chats,
        chatConfig,
        fetchCourierChatIframeUrl,
        fetchCourierChatState,
        openCourierChat,
        closeCourierChat,
      }}
    >
      {children}
    </CourierChatContext.Provider>
  )
}

export const useCourierChat = () => useContext(CourierChatContext)
